import type { Squad } from '@gonfalon/types/Squad';
import { v4 as uuid } from 'uuid';

import type { AnalyticEventData, TrackWithDetailsProps } from './types';

interface LegacyMetricNames {
  start?: string;
  track?: Record<string, string>;
  end?: string;
}

export interface EndOptions extends AnalyticEventData {
  outcome: 'success' | 'error' | 'cancel';
}

export interface Tracker {
  start: (attributes?: AnalyticEventData) => void;
  track: (name: string, attributes?: AnalyticEventData) => void;
  end: (options: EndOptions) => void;
}

export interface ExperienceStateEntry {
  experienceName: string;
  experienceId: string;
  owners?: Squad[];
  msFromDocumentLoad: number;
  duration: number;
}

export interface CreateOptions extends AnalyticEventData {
  category?: string;
  owners?: Squad[];
  emitLegacyMetrics?: boolean;
  legacyMetricNames?: LegacyMetricNames;
}

export interface ExperienceTracker {
  create: (experienceName: string, options?: CreateOptions) => Tracker;
  getState: () => ExperienceStateEntry[];
  clear: (experienceName: string) => void;
  endAll: (options?: EndOptions) => void;
}

interface ExperienceState {
  experienceName: string;
  experienceId: string;
  category?: string;
  owners?: Squad[];
  defaultAttributes?: AnalyticEventData;
  msFromDocumentLoad: number;
  emitLegacyMetrics?: boolean;
  legacyMetricNames?: LegacyMetricNames;
}

export interface ExperienceTrackerOptions {
  emit: (payload: TrackWithDetailsProps) => void;
  now?: () => number;
  warn?: (message: string) => void;
}

class ExperienceTrackerRuntime implements ExperienceTracker {
  private readonly inFlightExperiences = new Map<string, ExperienceState>();

  constructor(
    private readonly emit: (payload: TrackWithDetailsProps) => void,
    private readonly now: () => number,
    private readonly warn?: (message: string) => void,
  ) {}

  create(experienceName: string, options?: CreateOptions): Tracker {
    return new ExperienceTrackerInstance(this, experienceName, options);
  }

  getState(): ExperienceStateEntry[] {
    const elapsed = this.now();
    return Array.from(this.inFlightExperiences.values()).map((state) => ({
      experienceName: state.experienceName,
      experienceId: state.experienceId,
      owners: state.owners,
      msFromDocumentLoad: Math.round(state.msFromDocumentLoad),
      duration: Math.round(elapsed - state.msFromDocumentLoad),
    }));
  }

  clear(experienceName: string): void {
    for (const [id, state] of this.inFlightExperiences) {
      if (state.experienceName === experienceName) {
        this.endExperienceById(id);
      }
    }
  }

  endAll(options?: EndOptions): void {
    for (const id of this.inFlightExperiences.keys()) {
      this.endExperienceById(id, options);
    }
  }

  add(state: ExperienceState): void {
    this.inFlightExperiences.set(state.experienceId, state);
  }

  get(experienceId: string): ExperienceState | undefined {
    return this.inFlightExperiences.get(experienceId);
  }

  has(experienceId: string): boolean {
    return this.inFlightExperiences.has(experienceId);
  }

  remove(experienceId: string): void {
    this.inFlightExperiences.delete(experienceId);
  }

  timestamp(): number {
    return this.now();
  }

  emitEvent(event: string, category?: string, attributes?: AnalyticEventData): void {
    this.emit({ event, category, ...attributes });
  }

  reportWarning(message: string): void {
    this.warn?.(message);
  }

  private endExperienceById(experienceId: string, options?: EndOptions): void {
    const state = this.inFlightExperiences.get(experienceId);
    if (!state) {
      return;
    }
    const { outcome = 'cancel', ...attributes } = options ?? {};
    const duration = Math.round(this.now() - state.msFromDocumentLoad);
    this.inFlightExperiences.delete(experienceId);
    this.emit({
      event: `${state.experienceName} - End`,
      category: state.category,
      ...state.defaultAttributes,
      experienceName: state.experienceName,
      experienceId: state.experienceId,
      owners: state.owners,
      outcome,
      duration,
      ...attributes,
    });

    if (state.emitLegacyMetrics && state.legacyMetricNames?.end) {
      this.emit({ event: state.legacyMetricNames.end, category: state.category, outcome, ...attributes });
    }
  }
}

class ExperienceTrackerInstance implements Tracker {
  private readonly owners: Squad[] | undefined;
  private readonly category: string | undefined;
  private readonly emitLegacyMetrics: boolean | undefined;
  private readonly legacyMetricNames: LegacyMetricNames | undefined;
  private readonly defaultAttributes: AnalyticEventData;
  private currentExperienceId: string | undefined;

  constructor(
    private readonly runtime: ExperienceTrackerRuntime,
    private readonly experienceName: string,
    options?: CreateOptions,
  ) {
    const { owners, category, emitLegacyMetrics, legacyMetricNames, ...defaultAttributes } = options ?? {};
    this.owners = owners;
    this.category = category;
    this.emitLegacyMetrics = emitLegacyMetrics;
    this.legacyMetricNames = legacyMetricNames;
    this.defaultAttributes = defaultAttributes;
  }

  start(attributes?: AnalyticEventData): void {
    if (this.currentExperienceId && this.runtime.has(this.currentExperienceId)) {
      this.runtime.reportWarning(`Experience "${this.experienceName}" already started. Resetting.`);
      this.runtime.remove(this.currentExperienceId);
    }
    const experienceId = uuid();
    this.currentExperienceId = experienceId;
    this.runtime.add({
      experienceName: this.experienceName,
      experienceId,
      category: this.category,
      owners: this.owners,
      defaultAttributes: this.defaultAttributes,
      msFromDocumentLoad: this.runtime.timestamp(),
      emitLegacyMetrics: this.emitLegacyMetrics,
      legacyMetricNames: this.legacyMetricNames,
    });
    this.trackEvent(`${this.experienceName} - Start`, {
      ...this.mergeAttributes(attributes),
      msFromDocumentLoad: Math.round(this.runtime.timestamp()),
    });

    if (this.emitLegacyMetrics && this.legacyMetricNames?.start) {
      this.trackEvent(this.legacyMetricNames.start, attributes);
    }
  }

  track(name: string, attributes?: AnalyticEventData): void {
    if (this.currentExperienceId && !this.runtime.has(this.currentExperienceId)) {
      this.currentExperienceId = undefined;
    }
    const state = this.currentExperienceId ? this.runtime.get(this.currentExperienceId) : undefined;
    const msFromStart = state ? Math.round(this.runtime.timestamp() - state.msFromDocumentLoad) : undefined;
    this.trackEvent(`${this.experienceName} - ${name}`, {
      ...this.mergeAttributes(attributes),
      msFromStart,
    });

    if (this.emitLegacyMetrics) {
      const legacyName = this.legacyMetricNames?.track?.[name];
      if (legacyName) {
        this.trackEvent(legacyName, attributes);
      }
    }
  }

  end(options: EndOptions): void {
    const { outcome, ...attributes } = options;
    if (!this.currentExperienceId || !this.runtime.has(this.currentExperienceId)) {
      this.runtime.reportWarning(`Experience "${this.experienceName}" was never started.`);
      return;
    }
    const startTime = this.runtime.get(this.currentExperienceId)?.msFromDocumentLoad || 0;
    const duration = Math.round(this.runtime.timestamp() - startTime);
    const merged = this.mergeAttributes(attributes);
    this.runtime.remove(this.currentExperienceId);
    this.currentExperienceId = undefined;
    this.trackEvent(`${this.experienceName} - End`, { ...merged, outcome, duration });

    if (this.emitLegacyMetrics && this.legacyMetricNames?.end) {
      this.trackEvent(this.legacyMetricNames.end, attributes);
    }
  }

  private mergeAttributes(attributes?: AnalyticEventData): AnalyticEventData {
    return {
      experienceName: this.experienceName,
      experienceId: this.currentExperienceId,
      owners: this.owners,
      ...this.defaultAttributes,
      ...attributes,
    };
  }

  private trackEvent(event: string, attributes?: AnalyticEventData): void {
    this.runtime.emitEvent(event, this.category, attributes);
  }
}

export function createExperienceTracker({
  emit,
  now = () => performance.now(),
  warn,
}: ExperienceTrackerOptions): ExperienceTracker {
  return new ExperienceTrackerRuntime(emit, now, warn);
}
